/**
 * Small things a page remembers in this browser: an opened disclosure, a switch.
 *
 * Storage can be missing or blocked (a private window, a locked-down profile, the
 * server render), so every read and write is wrapped and falls back to a value kept for
 * this visit only, the same way `viewer-language.ts` does. Nothing here is state the app
 * depends on: a lost value only means the page opens as it does for a first visit.
 */

/** Values remembered while storage is unavailable: this visit only (and the test render). */
const thisVisit = new Map<string, string>();

/** The remembered value under `key`, or `fallback` when there is none or storage fails. */
export function readRemembered(key: string, fallback: string): string {
  try {
    const value = window.localStorage.getItem(key);
    return value == null ? fallback : value;
  } catch {
    return thisVisit.get(key) ?? fallback;
  }
}

/** Remember `value` under `key` in this browser, or for this visit without storage. */
export function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    thisVisit.set(key, value);
  }
}

/** Forget `key` (this browser and this visit); the page then opens as on a first visit. */
export function forget(key: string): void {
  thisVisit.delete(key);
  try {
    window.localStorage.removeItem(key);
  } catch {
    // No storage in this browser: nothing else to forget.
  }
}

/** The remembered JSON array of strings under `key`; `fallback` when missing or unreadable. */
export function readRememberedList(key: string, fallback: readonly string[]): string[] {
  const raw = readRemembered(key, "");
  if (raw === "") {
    return [...fallback];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((entry): entry is string => typeof entry === "string")
      : [...fallback];
  } catch {
    return [...fallback];
  }
}

/** Remember a list of strings as JSON under `key`. Fails silently without storage. */
export function rememberList(key: string, value: readonly string[]): void {
  remember(key, JSON.stringify(value));
}
